import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useCallback, useId, useMemo } from 'react';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { EscalationPolicyForm } from './escalation-policy-form';
import { POLICY_CATEGORY_ESCALATION } from './policy-constants';
import { SupportPolicyForm, SupportPolicyFormValues } from './policy-form';

export const SupportPolicyModal = () => {
  const { isOpened, modalProps } = useModalState(Modals.SupportPolicyModal);
  const { closeModal } = useModalMethods(Modals.SupportPolicyModal);
  const formId = useId();

  const handleClose = useCallback(() => {
    closeModal();
  }, [closeModal]);

  const handleSave = useCallback(
    (values: SupportPolicyFormValues) => {
      modalProps?.onSave?.(values, modalProps?.policy ?? null);
      closeModal();
    },
    [closeModal, modalProps],
  );

  const isEscalationPolicy = modalProps?.policy?.category === POLICY_CATEGORY_ESCALATION;
  const title = useMemo(() => {
    if (isEscalationPolicy) {
      return 'Edit escalation policy';
    }
    return modalProps?.policy?.id ? 'Edit policy' : 'Add policy';
  }, [modalProps?.policy?.id, isEscalationPolicy]);

  return (
    <Dialog
      open={Boolean(isOpened)}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          border: '1px solid',
          borderColor: (theme) => theme.palette.custom.OV.sys.outlineVariant,
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: '1px solid',
          borderColor: (theme) => theme.palette.custom.OV.sys.outlineVariant,
        }}
      >
        <Stack direction="row" spacing={2}>
          <Typography variant="overline">{title}</Typography>
        </Stack>
      </DialogTitle>

      <Box>
        <DialogContent sx={{ p: 2 }}>
          {isEscalationPolicy ? (
            <EscalationPolicyForm
              policy={modalProps?.policy ?? undefined}
              onSave={handleSave}
              formId={formId}
            />
          ) : (
            <SupportPolicyForm
              policy={modalProps?.policy ?? undefined}
              onSave={handleSave}
              formId={formId}
            />
          )}
        </DialogContent>
      </Box>

      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button form={formId} type="submit" variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
